import * as z from "zod";
import { gql } from "graphql-request";
import { CategorySchema } from "./category";

import {
  NewsEntryArticleSchema,
  NewsEntryArticleFragment,
} from "./newsEntryArticle";
import { Targeting, TargetingFragment } from "../targeting";
import { NewsEntryVideoSchema, NewsEntryVideoFragment } from "./newsEntryVideo";
import { NewsEntryImageSchema, NewsEntryImageFragment } from "./newsEntryImage";
import {
  NewsEntryExternalLinkSchema,
  NewsEntryExternalLinkFragment,
} from "./newsEntryExternalLink";
import {
  SmallNewsEntryArticleSchema,
  SmallNewsEntryArticleFragment,
} from "./smallNewsEntryArticle";
import {
  SmallNewsEntryExternalLinkSchema,
  SmallNewsEntryExternalLinkFragment,
} from "./smallNewsEntryExternalLink";

import { Sys, SysFragment } from "../sys";
import { RelatedMedia, RelatedMediaFragment } from "../relatedMedia";

export const NewsEntrySchema = z.object({
  sys: Sys,

  targeting: Targeting.nullish(),
  relatedMedia: RelatedMedia.nullish(),
  category: CategorySchema.nullable(),
  categoryEn: CategorySchema.nullish(),

  sortDate: z.string().nullable(),
  newUntil: z.string().nullable(),
  updatedUntil: z.string().nullish(),

  localized: z
    .discriminatedUnion("__typename", [
      NewsEntryArticleSchema,
      NewsEntryVideoSchema,
      NewsEntryImageSchema,
      NewsEntryExternalLinkSchema,
      SmallNewsEntryArticleSchema,
      SmallNewsEntryExternalLinkSchema,
    ])
    .nullish(),
});

export const NewsEntry = NewsEntrySchema;
export type NewsEntry = z.infer<typeof NewsEntrySchema>;

export const NewsEntryFragment = gql`
  fragment NewsEntryFragment on NewsEntry {
    sys {
      ...SysFragment
    }
    category {
      categoryId
      name
      color
      secondaryColor
    }

    # Only the name field is translatable: this is the fallback when a locale
    # has no translation for it yet (see transformNewsEntry).
    categoryEn: category(locale: "en") {
      categoryId
      name
      color
      secondaryColor
    }

    targeting {
      ...TargetingFragment
    }

    relatedMedia {
      ...RelatedMediaFragment
    }

    newUntil
    updatedUntil
    sortDate

    localized {
      __typename

      ... on NewsEntryVideo {
        ...NewsEntryVideoFragment
      }

      ... on NewsEntryImage {
        ...NewsEntryImageFragment
      }

      ... on NewsEntryExternalLink {
        ...NewsEntryExternalLinkFragment
      }

      ... on NewsEntryArticle {
        ...NewsEntryArticleFragment
      }

      ... on SmallNewsEntryArticle {
        ...SmallNewsEntryArticleFragment
      }

      ... on SmallNewsEntryExternalLink {
        ...SmallNewsEntryExternalLinkFragment
      }
    }
  }

  ${TargetingFragment}
  ${NewsEntryArticleFragment}
  ${NewsEntryVideoFragment}
  ${NewsEntryImageFragment}
  ${NewsEntryExternalLinkFragment}

  ${SmallNewsEntryExternalLinkFragment}
  ${SmallNewsEntryArticleFragment}
  ${SysFragment}
  ${RelatedMediaFragment}
`;
